import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { AnnouncementPageType, AnnouncementType } from '@type/announcement.type';

import { AppIconComponent } from '@components/@fec/app-icon.component';

import { AnnouncementService } from '@services/api/announcement.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * Header notification bell for announcements (FEC-1161 QA).
 *
 * Every signed-in role gets one — announcements previously had no entry point
 * outside a project's own Announcements tab, so an agent had no way to know
 * something new had been published until they happened to open the project.
 *
 * The badge counts unread announcements across every project the user can open;
 * picking one from the dropdown navigates to that project's Announcements tab.
 */
@Component({
  selector: 'app-announcement-bell',
  imports: [DatePipe, AppIconComponent],
  templateUrl: './announcement-bell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'onDocumentClick($event)' },
})
export class AnnouncementBellComponent {
  private readonly announcementService = inject(AnnouncementService);
  private readonly userPermissionService = inject(UserPermissionService);
  private readonly router = inject(Router);

  /** Host element, used to tell inside-clicks from outside-clicks. */
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  protected readonly isOpen = signal<boolean>(false);

  /* `enabled` is a computed signal (not a bare closure): TanStack's Angular
     adapter only re-evaluates the gate reactively when it is a Signal, so a
     closure that first resolved false — before `/me` answered — stayed false
     and the bell never fetched. */
  private readonly unreadQuery = this.announcementService.queryGetUnreadAnnouncementCount({
    enabled: computed(() => this.userPermissionService.isAuthenticated()),
    refetchOnWindowFocus: true,
    select: (response: any) => {
      const { status, data } = (response ?? {}) as {
        status?: boolean;
        data?: { unread_count?: number };
      };
      return status ? (data?.unread_count ?? 0) : 0;
    },
  });

  /*
   * Fetched alongside the badge rather than on first open. Gating this on
   * `isOpen()` meant the panel could show "You have no announcements yet" while
   * the badge said 44 — the list simply had not been requested yet (FEC-1161 QA).
   *
   * 20, not 10: the service requests unread-first, so as long as unread count
   * stays under this the dropdown always contains every unread row — otherwise
   * the badge and the visible list disagree (FEC-1161 QA, Hina 12 Aug).
   */
  private readonly recentQuery = this.announcementService.queryGetRecentAnnouncements(20, {
    enabled: computed(() => this.userPermissionService.isAuthenticated()),
    refetchOnWindowFocus: true,
    select: (response: any) => {
      const { status, data } = (response ?? {}) as {
        status?: boolean;
        data?: AnnouncementPageType;
      };
      return status ? (data?.data ?? []) : [];
    },
  });

  protected readonly unreadCount = computed<number>(() => (this.unreadQuery.data() as number) ?? 0);
  protected readonly announcements = computed<AnnouncementType[]>(
    () => (this.recentQuery.data() as AnnouncementType[]) ?? [],
  );
  protected readonly isLoading = computed(() => this.recentQuery.isFetching());

  private readonly markReadMutation = this.announcementService.markAnnouncementReadMutation({
    onSuccess: () => this.announcementService.invalidateAnnouncements(),
  });

  private readonly markAllReadMutation = this.announcementService.markAllAnnouncementsReadMutation({
    onSuccess: () => this.announcementService.invalidateAnnouncements(),
  });

  protected readonly isMarkingAll = computed(() => this.markAllReadMutation.isPending());

  /** Badge caps at 99+ so a long-idle account cannot stretch the header. */
  protected readonly badgeLabel = computed(() => {
    const count = this.unreadCount();
    return count > 99 ? '99+' : `${count}`;
  });

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  /** Closes the panel when the click landed outside this component. */
  protected onDocumentClick(event: Event): void {
    if (!this.isOpen()) return;
    const host = this.elementRef?.nativeElement;
    if (host && !host.contains(event.target as Node)) {
      this.close();
    }
  }

  /**
   * Opens the announcement itself, not just the project it belongs to.
   *
   * The `announcement` query param is what the Announcements tab reads to slide
   * that one open on arrival — clicking a notification used to drop the user on
   * the list and leave them to find it (FEC-1161 QA).
   */
  protected openAnnouncement(announcement: AnnouncementType): void {
    this.close();

    if (!announcement.is_read) {
      this.markReadMutation.mutate({ id: announcement.id });
    }

    this.router.navigate(['/project-detail', announcement.project_id, 'announcement'], {
      queryParams: { announcement: announcement.id },
    });
  }

  /** Clears the badge by marking everything this user receives as read. */
  protected markAllAsRead(): void {
    if (this.markAllReadMutation.isPending() || this.unreadCount() === 0) return;
    this.markAllReadMutation.mutate({ asRecipient: true });
  }

  /** Plain-text preview of the rich-text body for the dropdown row. */
  protected messagePreview(announcement: AnnouncementType): string {
    const text = (announcement.message ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
  }
}
